import { useMutation, useQuery } from '@apollo/client';
import { Player } from '@lottiefiles/react-lottie-player';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { GET_BADGES } from '../../../../liveops/components/apolloQueries';
import {
  GRANT_USER_BADGE_ADMIN,
  REVOKE_USER_BADGE_ADMIN,
  SET_USER_BADGE_LEVEL_ADMIN,
  USER_BADGES_ADMIN,
} from '../../../graphql';
import ItemsAdd from './ItemsAdd';

type BadgeLevelDetail = { level: number; asset?: string | null };

type UserBadgeAdmin = {
  id: string;
  name: string;
  type: string;
  owned: boolean;
  isSP2: boolean;
  currentLevel?: number | null;
  details?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UserBadgesAdminQuery = {
  userBadgesAdmin?: UserBadgeAdmin[];
};

type UserBadgesAdminVariables = {
  userId: string;
};

type BadgeConfig = {
  id: string;
  name: string;
  type: string;
  details?: string | null;
};

type GetBadgesQuery = {
  liveOps?: {
    badgeConfigs?: {
      list?: BadgeConfig[];
    };
  };
};

type Props = {
  userId: string;
};

export type UserBadgesDisplayHandle = {
  flush: () => Promise<void>;
  hasPendingChanges: () => boolean;
};

const parseBadgeDetails = (raw?: string | null): { levelDetails: BadgeLevelDetail[]; asset?: string | null } => {
  if (!raw) return { levelDetails: [] };
  try {
    const parsed = JSON.parse(raw);
    return {
      levelDetails: Array.isArray(parsed?.levelDetails) ? parsed.levelDetails : [],
      asset: parsed?.asset,
    };
  } catch {
    return { levelDetails: [] };
  }
};

const assetForBadge = (
  details: { levelDetails: BadgeLevelDetail[]; asset?: string | null },
  isSp2: boolean,
  currentLevel: number
): string | null => {
  if (isSp2) return details.asset ?? null;
  return details.levelDetails[currentLevel]?.asset ?? details.levelDetails[0]?.asset ?? null;
};

// Badge.id is a composite "userId.appId.realBadgeId" (see UserBadgeManager.getId).
// Mutations expect the raw badge id, matching the mobile client which does `badge.id.split('.')[2]`.
const rawBadgeId = (compositeId: string): string => {
  const parts = compositeId.split('.');
  return parts.length >= 3 ? parts.slice(2).join('.') : compositeId;
};

const BadgePreview = ({ assetUrl }: { assetUrl: string | null }) => {
  if (!assetUrl) return null;
  if (assetUrl.endsWith('.json')) {
    return (
      <span
        className="img-profile mr-3"
        style={{ display: 'inline-block', width: 30, height: 30, verticalAlign: 'middle' }}
      >
        <Player autoplay loop src={assetUrl} style={{ width: 30, height: 30 } as any} />
      </span>
    );
  }
  return (
    <img
      src={assetUrl}
      alt=""
      className="img-profile mr-3 rounded-circle"
      style={{ width: 30, height: 30, objectFit: 'contain', verticalAlign: 'middle' }}
    />
  );
};

type DisplayBadge = {
  key: string;
  rawId: string;
  name: string;
  type: string;
  isSP2: boolean;
  details?: string | null;
  level: number;
  isPendingGrant: boolean;
  isPendingRevoke: boolean;
  isPendingLevel: boolean;
};

const UserBadgesDisplay = forwardRef<UserBadgesDisplayHandle, Props>(({ userId }, ref) => {
  const [flushError, setFlushError] = useState<string | null>(null);

  const { data, refetch } = useQuery<UserBadgesAdminQuery, UserBadgesAdminVariables>(USER_BADGES_ADMIN, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'no-cache',
  });

  const { data: catalogData } = useQuery<GetBadgesQuery>(GET_BADGES, { fetchPolicy: 'cache-first' });
  const badgeCatalog = catalogData?.liveOps?.badgeConfigs?.list ?? [];

  const [grantBadge] = useMutation(GRANT_USER_BADGE_ADMIN);
  const [revokeBadge] = useMutation(REVOKE_USER_BADGE_ADMIN);
  const [setBadgeLevel] = useMutation(SET_USER_BADGE_LEVEL_ADMIN);

  // Pending edits, all keyed by raw badge id. Flushed on Save User.
  const [pendingGrants, setPendingGrants] = useState<Record<string, number>>({});
  const [pendingRevokes, setPendingRevokes] = useState<Set<string>>(new Set());
  const [pendingLevels, setPendingLevels] = useState<Record<string, number>>({});

  const serverOwned = (data?.userBadgesAdmin ?? []).filter((b) => b.owned);

  const ownedRawIds = useMemo(() => {
    const ids = new Set<string>();
    serverOwned.forEach((b) => {
      const raw = rawBadgeId(b.id);
      if (!pendingRevokes.has(raw)) ids.add(raw);
    });
    Object.keys(pendingGrants).forEach((id) => ids.add(id));
    return ids;
  }, [serverOwned, pendingRevokes, pendingGrants]);

  const grantableItems = badgeCatalog
    .filter((b) => !ownedRawIds.has(b.id) && !b.id.startsWith('sp2_'))
    .map((b) => ({ id: b.id, name: b.name, level: 0 }));

  const hasPending = (): boolean =>
    Object.keys(pendingGrants).length > 0 ||
    pendingRevokes.size > 0 ||
    Object.keys(pendingLevels).length > 0;

  useImperativeHandle(
    ref,
    () => ({
      hasPendingChanges: hasPending,
      async flush() {
        if (!hasPending()) return;
        setFlushError(null);
        try {
          const grantOps = Object.entries(pendingGrants).map(([badgeId, level]) =>
            grantBadge({ variables: { input: { userId, badgeId, level } } })
          );
          const revokeOps: Promise<unknown>[] = [];
          pendingRevokes.forEach((badgeId) => {
            revokeOps.push(revokeBadge({ variables: { input: { userId, badgeId } } }));
          });
          const levelOps = Object.entries(pendingLevels).map(([badgeId, level]) =>
            setBadgeLevel({ variables: { input: { userId, badgeId, level } } })
          );
          await Promise.all([...grantOps, ...revokeOps, ...levelOps]);
          setPendingGrants({});
          setPendingRevokes(new Set());
          setPendingLevels({});
          await refetch();
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'unknown error';
          setFlushError(msg);
          throw err;
        }
      },
    }),
    [pendingGrants, pendingRevokes, pendingLevels, userId, grantBadge, revokeBadge, setBadgeLevel, refetch]
  );

  const handleAdd = (item: { id: string; level: number }) => {
    setPendingGrants((prev) => ({ ...prev, [item.id]: 0 }));
  };

  const handleAddAll = (items: { id: string; level: number }[]) => {
    setPendingGrants((prev) => {
      const next = { ...prev };
      items.forEach((item) => {
        next[item.id] = 0;
      });
      return next;
    });
  };

  const handleRevoke = (rawId: string, isPendingGrant: boolean) => {
    if (isPendingGrant) {
      setPendingGrants((prev) => {
        const next = { ...prev };
        delete next[rawId];
        return next;
      });
      return;
    }
    setPendingRevokes((prev) => new Set(prev).add(rawId));
    setPendingLevels((prev) => {
      if (!(rawId in prev)) return prev;
      const next = { ...prev };
      delete next[rawId];
      return next;
    });
  };

  const handleUndoRevoke = (rawId: string) => {
    setPendingRevokes((prev) => {
      if (!prev.has(rawId)) return prev;
      const next = new Set(prev);
      next.delete(rawId);
      return next;
    });
  };

  const handleChangeLevel = (rawId: string, level: number, isPendingGrant: boolean) => {
    if (isPendingGrant) {
      setPendingGrants((prev) => ({ ...prev, [rawId]: level }));
      return;
    }
    const original = serverOwned.find((b) => rawBadgeId(b.id) === rawId)?.currentLevel ?? 0;
    setPendingLevels((prev) => {
      if (level === original) {
        if (!(rawId in prev)) return prev;
        const next = { ...prev };
        delete next[rawId];
        return next;
      }
      return { ...prev, [rawId]: level };
    });
  };

  const displayBadges: DisplayBadge[] = useMemo(() => {
    const fromServer: DisplayBadge[] = serverOwned.map((b) => {
      const raw = rawBadgeId(b.id);
      return {
        key: b.id,
        rawId: raw,
        name: b.name,
        type: b.type,
        isSP2: b.isSP2,
        details: b.details,
        level: pendingLevels[raw] ?? b.currentLevel ?? 0,
        isPendingGrant: false,
        isPendingRevoke: pendingRevokes.has(raw),
        isPendingLevel: pendingLevels[raw] !== undefined,
      };
    });
    const fromPending: DisplayBadge[] = Object.entries(pendingGrants).map(([rawId, level]) => {
      const cat = badgeCatalog.find((c) => c.id === rawId);
      return {
        key: `pending:${rawId}`,
        rawId,
        name: cat?.name ?? rawId,
        type: cat?.type ?? '',
        isSP2: false,
        details: cat?.details,
        level,
        isPendingGrant: true,
        isPendingRevoke: false,
        isPendingLevel: false,
      };
    });
    return [...fromServer, ...fromPending];
  }, [serverOwned, badgeCatalog, pendingGrants, pendingRevokes, pendingLevels]);

  const pendingCount =
    Object.keys(pendingGrants).length + pendingRevokes.size + Object.keys(pendingLevels).length;

  return (
    <Form.Group>
      <div className="d-flex justify-content-between align-items-center uv-items-header">
        <div className="uv-items-header-side" />
        <Form.Label className="uv-items-title">
          badges{' '}
          {pendingCount > 0 && (
            <Badge variant="warning">{pendingCount} unsaved</Badge>
          )}
        </Form.Label>
        <div className="uv-items-header-side d-flex justify-content-end">
          <ItemsAdd items={grantableItems} type="badges" onAdd={handleAdd} onAddAll={handleAddAll} />
        </div>
      </div>
      {flushError && (
        <Alert variant="danger" className="py-2">
          Save badges failed: {flushError}
        </Alert>
      )}
      {displayBadges.length > 0 ? (
        <Row>
          {displayBadges.map((b) => {
            const parsed = parseBadgeDetails(b.details);
            const preview = assetForBadge(parsed, b.isSP2, b.level);
            const levelOptions =
              parsed.levelDetails.length > 0
                ? parsed.levelDetails.map((_ld, idx) => idx + 1)
                : [b.level + 1];
            const cardStyle: React.CSSProperties = b.isPendingRevoke
              ? { opacity: 0.5, textDecoration: 'line-through' }
              : {};
            return (
              <Col key={b.key} md="auto" className="mb-4">
                <Card style={cardStyle}>
                  <Card.Header>
                    <BadgePreview assetUrl={preview} />
                    {b.name}
                    {b.isPendingGrant && (
                      <Badge variant="info" className="ml-2">
                        new
                      </Badge>
                    )}
                    {b.isPendingLevel && (
                      <Badge variant="warning" className="ml-2">
                        level *
                      </Badge>
                    )}
                    {b.isPendingRevoke && (
                      <Badge variant="danger" className="ml-2">
                        revoking
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body className="d-flex flex-column">
                    <Form.Group className="d-flex mb-3 align-items-baseline">
                      <Form.Label className="mr-2">Level: </Form.Label>
                      <Form.Control
                        as="select"
                        value={b.level + 1}
                        disabled={b.isSP2 || b.isPendingRevoke}
                        onChange={(e) =>
                          handleChangeLevel(b.rawId, Number(e.target.value) - 1, b.isPendingGrant)
                        }
                      >
                        {levelOptions.map((lvl) => (
                          <option key={lvl} value={lvl}>
                            {lvl}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    {b.isPendingRevoke ? (
                      <Button variant="outline-secondary" onClick={() => handleUndoRevoke(b.rawId)}>
                        Undo Remove
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        onClick={() => handleRevoke(b.rawId, b.isPendingGrant)}
                        disabled={b.isSP2}
                        title={b.isSP2 ? 'SP2 badges cannot be revoked from admin' : undefined}
                      >
                        Remove
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <p>
          <i>No badges</i>
        </p>
      )}
    </Form.Group>
  );
});

UserBadgesDisplay.displayName = 'UserBadgesDisplay';

export default UserBadgesDisplay;
